import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AlertOctagon, Bell, Info } from "lucide-react";
import { format } from 'date-fns';
import { AlertsFilters } from './AlertsFilters';
import { DateRange } from 'react-day-picker';

interface Filters {
  severity?: string;
  category?: string;
  dateRange?: DateRange;
}

interface AlertGroup {
  severity: string;
  alerts: any[];
}

export const AlertsPanel = () => {
  const [filters, setFilters] = useState<Filters>({});

  const { data: alerts } = useQuery({
    queryKey: ['system-alerts', filters],
    queryFn: async () => {
      let query = supabase
        .from('system_alerts')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters.severity) {
        query = query.eq('severity', filters.severity);
      }

      if (filters.category) {
        query = query.eq('category', filters.category);
      }

      if (filters.dateRange?.from) {
        query = query.gte('created_at', filters.dateRange.from.toISOString());
      }

      if (filters.dateRange?.to) {
        query = query.lte('created_at', filters.dateRange.to.toISOString());
      }

      const { data, error } = await query.limit(50);

      if (error) throw error;
      return data;
    },
    refetchInterval: 5000
  });

  const getAlertIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertOctagon className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <Bell className="h-5 w-5 text-yellow-500" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  const handleFilterChange = (newFilters: Filters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const groupAlertsBySeverity = (alerts: any[]): AlertGroup[] => {
    if (!alerts) return [];
    
    const groups: { [key: string]: any[] } = {};
    alerts.forEach(alert => {
      if (!groups[alert.severity]) {
        groups[alert.severity] = [];
      }
      groups[alert.severity].push(alert);
    });

    return Object.entries(groups).map(([severity, alerts]) => ({
      severity,
      alerts
    }));
  };

  const alertGroups = groupAlertsBySeverity(alerts || []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Alertas do Sistema</CardTitle>
      </CardHeader>
      <CardContent>
        <AlertsFilters onFilterChange={handleFilterChange} />
        <div className="space-y-6">
          {alertGroups.map((group) => (
            <div key={group.severity} className="space-y-4">
              <h3 className="font-medium capitalize">
                {group.severity === 'critical' ? 'Críticos' : 
                 group.severity === 'warning' ? 'Alertas' : 'Informações'}
                <span className="ml-2 text-sm text-muted-foreground">
                  ({group.alerts.length})
                </span>
              </h3>
              {group.alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`flex items-start space-x-4 p-4 rounded-lg ${
                    alert.severity === 'critical'
                      ? 'bg-red-50'
                      : alert.severity === 'warning'
                      ? 'bg-yellow-50'
                      : 'bg-blue-50'
                  }`}
                >
                  {getAlertIcon(alert.severity)}
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{alert.message}</p>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(alert.created_at), 'dd/MM/yyyy HH:mm:ss')}
                        </p>
                      </div>
                      {alert.category && (
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {alert.category}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
          {(!alerts || alerts.length === 0) && (
            <p className="text-center text-muted-foreground">
              Nenhum alerta encontrado
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};